import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  Mail,
  Phone,
  Briefcase,
  Linkedin,
  Users,
  Sparkles,
  Check,
  ChevronsUpDown,
  X,
  Building2,
  Pencil,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useSearchShortcut } from "@/lib/use-search-shortcut";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";

type EnrichField = "email" | "phone" | "jobTitle" | "linkedinUrl";
type TabKey = "ready" | "data-missing";

type ClientOption = { id: string; companyName: string };

type ContactRow = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  mobilePhone: string | null;
  jobTitle: string | null;
  linkedinUrl: string | null;
  clientId: string | null;
  clientName: string | null;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validateEmail(v: string): string | null {
  return EMAIL_RE.test(v) ? null : "Invalid email";
}

function normalizeUrl(v: string): string {
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
}

function validateUrl(v: string): string | null {
  try {
    new URL(normalizeUrl(v));
    return null;
  } catch {
    return "Invalid URL";
  }
}

function isContactReady(c: ContactRow): boolean {
  return Boolean(c.firstName && c.lastName && c.email && (c.phone || c.mobilePhone));
}

function ClientCombobox({
  value,
  valueLabel,
  options,
  isLoading,
  onChange,
  disabled,
}: {
  value: string | null;
  valueLabel: string | null;
  options: ClientOption[];
  isLoading: boolean;
  onChange: (id: string | null) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);

  const label = valueLabel ?? options.find((o) => o.id === value)?.companyName ?? null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        type="button"
        disabled={disabled}
        className={cn(
          "inline-flex h-8 min-w-0 max-w-[220px] items-center justify-between gap-1 rounded-md border border-transparent px-2 text-sm hover:border-input hover:bg-muted/40 focus:outline-none focus:ring-2 focus:ring-ring",
          !label && "text-muted-foreground"
        )}
      >
        <span className="flex min-w-0 items-center gap-1.5">
          <Building2 className="h-3.5 w-3.5 shrink-0 opacity-60" />
          <span className="truncate">{label ?? "Assign client"}</span>
        </span>
        <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command
          filter={(itemValue, search) =>
            itemValue.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
          }
        >
          <CommandInput placeholder="Search clients..." />
          <CommandList>
            {isLoading ? (
              <div className="p-3 text-center text-sm text-muted-foreground">
                Loading...
              </div>
            ) : (
              <>
                <CommandEmpty>No clients found.</CommandEmpty>
                {value ? (
                  <>
                    <CommandGroup>
                      <CommandItem
                        value="__clear__ remove unassign"
                        onSelect={() => {
                          onChange(null);
                          setOpen(false);
                        }}
                        className="text-muted-foreground"
                      >
                        <X className="mr-2 h-4 w-4" />
                        Unassign client
                      </CommandItem>
                    </CommandGroup>
                    <CommandSeparator />
                  </>
                ) : null}
                <CommandGroup>
                  {options.map((c) => (
                    <CommandItem
                      key={c.id}
                      value={`${c.companyName} ${c.id}`}
                      onSelect={() => {
                        onChange(c.id);
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === c.id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <span className="truncate">{c.companyName}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function ContactsPage() {
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<TabKey>("data-missing");
  const searchInputRef = useSearchShortcut();
  const utils = trpc.useUtils();
  const list = trpc.contacts.list.useQuery({ search: search || undefined });
  const clientsQuery = trpc.clients.list.useQuery();
  const rows = list.data ?? [];

  const clientOptions = useMemo<ClientOption[]>(
    () =>
      (clientsQuery.data ?? [])
        .map((c) => ({ id: c.id, companyName: c.companyName }))
        .sort((a, b) => a.companyName.localeCompare(b.companyName)),
    [clientsQuery.data]
  );

  const assign = trpc.contacts.assignToClient.useMutation({
    onSuccess: () => {
      toast.success("Client assigned");
      utils.contacts.list.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const runChain = trpc.contacts.runEnrichmentChain.useMutation({
    onSuccess: (_, vars) => {
      toast.success("Paleistas duomenų papildymas");
      utils.contacts.list.invalidate();
      utils.contacts.listEnrichmentRuns.invalidate({ contactId: vars.contactId });
    },
    onError: (err) => toast.error(err.message),
  });

  const update = trpc.contacts.update.useMutation({
    onSuccess: () => {
      utils.contacts.list.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const enrich = (contactId: string, field: EnrichField) =>
    runChain.mutate({ contactId, field });

  const save = (
    id: string,
    patch: Partial<Record<"firstName" | "lastName" | "email" | "phone" | "jobTitle" | "linkedinUrl", string | null>>
  ) => update.mutate({ id, patch });

  const readyCount = useMemo(() => rows.filter(isContactReady).length, [rows]);
  const missingCount = rows.length - readyCount;

  const filtered = useMemo(
    () =>
      rows.filter((c) => (tab === "ready" ? isContactReady(c) : !isContactReady(c))),
    [rows, tab]
  );

  return (
    <div className="mx-auto w-full max-w-screen-2xl px-4 py-6 md:px-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Contacts</h1>
          <p className="text-sm text-muted-foreground">
            {rows.length} total · {readyCount} ready · {missingCount} missing data
          </p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={searchInputRef}
            placeholder="Search by name, email, client..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)} className="mb-3">
        <TabsList>
          <TabsTrigger value="ready" className="gap-1.5">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Ready
            <span className="ml-0.5 rounded bg-muted px-1.5 text-[11px] font-normal text-muted-foreground data-[active=true]:bg-muted-foreground/10">
              {readyCount}
            </span>
          </TabsTrigger>
          <TabsTrigger value="data-missing" className="gap-1.5">
            <AlertCircle className="h-3.5 w-3.5" />
            Data Missing
            <span className="ml-0.5 rounded bg-red-100 px-1.5 text-[11px] font-normal text-red-700 data-[active=true]:bg-red-200">
              {missingCount}
            </span>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {list.isLoading ? (
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed p-12 text-center">
          <Users className="mx-auto h-10 w-10 text-muted-foreground/30" />
          <p className="mt-4 text-sm text-muted-foreground">
            No contacts. Import from HubSpot or add manually.
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed p-12 text-center">
          {tab === "ready" ? (
            <CheckCircle2 className="mx-auto h-10 w-10 text-muted-foreground/30" />
          ) : (
            <AlertCircle className="mx-auto h-10 w-10 text-muted-foreground/30" />
          )}
          <p className="mt-4 text-sm text-muted-foreground">
            {tab === "ready"
              ? "No contacts have all data points yet."
              : "All contacts have complete data."}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-background">
          <table className="w-full min-w-[920px] text-sm">
            <thead className="border-b bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Name</th>
                <th className="px-4 py-2 text-left font-medium">Title</th>
                <th className="px-4 py-2 text-left font-medium">Email</th>
                <th className="px-4 py-2 text-left font-medium">Phone</th>
                <th className="px-4 py-2 text-left font-medium">LinkedIn</th>
                <th className="px-4 py-2 text-left font-medium">Client</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const pendingEnrich =
                  runChain.isPending && runChain.variables?.contactId === c.id;
                const pendingAssign =
                  assign.isPending && assign.variables?.id === c.id;
                return (
                  <tr key={c.id} className="border-b last:border-b-0 hover:bg-muted/30">
                    <td className="px-4 py-2 font-medium">
                      <NameCell
                        contactId={c.id}
                        firstName={c.firstName}
                        lastName={c.lastName}
                        email={c.email}
                        onSave={(patch) => save(c.id, patch)}
                      />
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">
                      <EditableCell
                        value={c.jobTitle}
                        onSave={(v) => save(c.id, { jobTitle: v })}
                        icon={<Briefcase className="h-3.5 w-3.5" />}
                        placeholder="Add title"
                        pending={pendingEnrich}
                        onEnrich={() => enrich(c.id, "jobTitle")}
                        enrichTitle="Papildyti pareigas"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <EditableCell
                        value={c.email}
                        onSave={(v) => save(c.id, { email: v })}
                        icon={<Mail className="h-3.5 w-3.5" />}
                        type="email"
                        validate={validateEmail}
                        placeholder="Add email"
                        pending={pendingEnrich}
                        onEnrich={() => enrich(c.id, "email")}
                        enrichTitle="Papildyti el. paštą"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <EditableCell
                        value={c.phone ?? c.mobilePhone}
                        onSave={(v) => save(c.id, { phone: v })}
                        icon={<Phone className="h-3.5 w-3.5" />}
                        type="tel"
                        placeholder="Add phone"
                        pending={pendingEnrich}
                        onEnrich={() => enrich(c.id, "phone")}
                        enrichTitle="Papildyti telefoną"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <EditableCell
                        value={c.linkedinUrl}
                        onSave={(v) =>
                          save(c.id, { linkedinUrl: v ? normalizeUrl(v) : null })
                        }
                        icon={<Linkedin className="h-3.5 w-3.5" />}
                        type="url"
                        validate={validateUrl}
                        placeholder="Add LinkedIn"
                        pending={pendingEnrich}
                        onEnrich={() => enrich(c.id, "linkedinUrl")}
                        enrichTitle="Papildyti LinkedIn"
                        renderValue={(v) => (
                          <a
                            href={v}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="truncate hover:underline"
                          >
                            {prettyLinkedin(v)}
                          </a>
                        )}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-1">
                        <ClientCombobox
                          value={c.clientId ?? null}
                          valueLabel={c.clientName ?? null}
                          options={clientOptions}
                          isLoading={clientsQuery.isLoading}
                          disabled={pendingAssign}
                          onChange={(clientId) =>
                            assign.mutate({ id: c.id, clientId })
                          }
                        />
                        {c.clientId && c.clientName ? (
                          <Link
                            to={`/clients/${c.clientId}`}
                            className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                            title="Open client"
                          >
                            ↗
                          </Link>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function prettyLinkedin(url: string): string {
  try {
    const u = new URL(url);
    return (u.host + u.pathname).replace(/^www\./, "");
  } catch {
    return url;
  }
}

function NameCell({
  contactId,
  firstName,
  lastName,
  email,
  onSave,
}: {
  contactId: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  onSave: (patch: { firstName?: string | null; lastName?: string | null }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [first, setFirst] = useState(firstName ?? "");
  const [last, setLast] = useState(lastName ?? "");

  useEffect(() => {
    if (!open) {
      setFirst(firstName ?? "");
      setLast(lastName ?? "");
    }
  }, [firstName, lastName, open]);

  const fullName = [firstName, lastName].filter(Boolean).join(" ");
  const display = fullName || email || "—";

  const commit = () => {
    const f = first.trim() || null;
    const l = last.trim() || null;
    const fChanged = f !== (firstName ?? null);
    const lChanged = l !== (lastName ?? null);
    if (fChanged || lChanged) {
      const patch: { firstName?: string | null; lastName?: string | null } = {};
      if (fChanged) patch.firstName = f;
      if (lChanged) patch.lastName = l;
      onSave(patch);
    }
    setOpen(false);
  };

  return (
    <div className="group flex min-w-0 items-center gap-1">
      <Link
        to={`/contacts/${contactId}`}
        className={cn(
          "min-w-0 truncate hover:text-primary hover:underline",
          !fullName && "italic text-muted-foreground"
        )}
      >
        {display}
      </Link>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            title="Edit name"
            className={cn(
              "shrink-0 rounded p-1 text-muted-foreground transition",
              "opacity-60 group-hover:opacity-100 focus:opacity-100 md:opacity-0",
              "hover:bg-muted hover:text-foreground"
            )}
          >
            <Pencil className="h-3 w-3" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 space-y-3 p-3" align="start">
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground">
              First name
            </label>
            <Input
              autoFocus
              value={first}
              onChange={(e) => setFirst(e.target.value)}
              className="h-8"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commit();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  setOpen(false);
                }
              }}
            />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Last name
            </label>
            <Input
              value={last}
              onChange={(e) => setLast(e.target.value)}
              className="h-8"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commit();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  setOpen(false);
                }
              }}
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={commit}>
              Save
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function EditableCell({
  value,
  onSave,
  type = "text",
  validate,
  placeholder,
  icon,
  renderValue,
  pending,
  onEnrich,
  enrichTitle,
}: {
  value: string | null;
  onSave: (next: string | null) => void;
  type?: "text" | "email" | "url" | "tel";
  validate?: (v: string) => string | null;
  placeholder?: string;
  icon?: React.ReactNode;
  renderValue?: (v: string) => React.ReactNode;
  pending?: boolean;
  onEnrich?: () => void;
  enrichTitle?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!editing) {
      setDraft(value ?? "");
      setError(null);
    }
  }, [value, editing]);

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed && validate) {
      const err = validate(trimmed);
      if (err) {
        setError(err);
        return;
      }
    }
    const next = trimmed === "" ? null : trimmed;
    if (next !== (value ?? null)) onSave(next);
    setEditing(false);
    setError(null);
  };

  const cancel = () => {
    setDraft(value ?? "");
    setEditing(false);
    setError(null);
  };

  if (editing) {
    return (
      <div className="relative">
        <Input
          autoFocus
          type={type}
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              (e.currentTarget as HTMLInputElement).blur();
            } else if (e.key === "Escape") {
              e.preventDefault();
              cancel();
            }
          }}
          className="h-7 px-2 text-sm"
        />
        {error && (
          <div className="absolute left-0 top-full z-10 mt-0.5 rounded bg-destructive px-2 py-0.5 text-[11px] text-destructive-foreground">
            {error}
          </div>
        )}
      </div>
    );
  }

  const isEmpty = !value;

  return (
    <div className="group flex items-center justify-between gap-1">
      <button
        type="button"
        onClick={() => setEditing(true)}
        title={value ? "Click to edit" : placeholder ?? "Click to add"}
        className={cn(
          "-mx-1 flex min-w-0 flex-1 items-center gap-1 truncate rounded px-1 py-0.5 text-left hover:bg-muted/60",
          isEmpty && "text-muted-foreground"
        )}
      >
        {value ? (
          renderValue ? (
            <span className="inline-flex min-w-0 items-center gap-1 truncate">
              {icon}
              {renderValue(value)}
            </span>
          ) : (
            <span className="inline-flex min-w-0 items-center gap-1 truncate">
              {icon}
              <span className="truncate">{value}</span>
            </span>
          )
        ) : (
          <span className="text-muted-foreground/70">{placeholder ?? "—"}</span>
        )}
      </button>
      {onEnrich && (
        <button
          type="button"
          onClick={onEnrich}
          disabled={pending}
          title={enrichTitle}
          className={cn(
            "shrink-0 rounded p-1 text-muted-foreground transition",
            isEmpty
              ? "opacity-60 group-hover:opacity-100 md:opacity-40"
              : "opacity-60 group-hover:opacity-100 focus:opacity-100 md:opacity-0",
            "hover:bg-muted hover:text-foreground",
            "disabled:cursor-not-allowed disabled:opacity-30"
          )}
        >
          <Sparkles className={cn("h-3 w-3", pending && "animate-pulse")} />
        </button>
      )}
    </div>
  );
}
